import type { ReactElement } from 'react';
import type { SchoolProfilesResponse } from '@/lib/types';
import { BarList, type BarListRow } from '../fab-ui';
import { PiStat, PiStatGrid } from '../pi-kit';
import { formatCount } from '../use-fab-data';
import {
  countryRows,
  interactionRows,
  sourceRows,
  viewsBreakdownOk,
  viewsChannelRows,
  viewsCountryRows,
} from './school-profiles-model';

/**
 * School Profiles headline and Overview tab.
 *
 * - `SchoolProfilesStats`: four tiles (page-wide, above the tabs). Visitors
 *   ride as the Profile views detail and public profiles as the Subscribed
 *   schools detail, so nothing is shown twice.
 * - The Overview tab, as three layout-manager blocks the page arranges
 *   (2026-10-20): `SchoolProfilesEngagement` (the interaction mix, where
 *   engaged students are from, and where they arrived from: UTM source, medium
 *   as the hint), then, when `meta.viewsBreakdownStatus === 'ok'`,
 *   `SchoolProfilesViewsByCountry` and `SchoolProfilesViewsByChannel` (each
 *   renders nothing when the server omits it; `null` → "—").
 *
 * Every figure is the server's cycle-wide answer for the chosen tier, taken
 * from the first page's response, so paging the school table never moves it.
 * Errors are rendered by the page.
 */

const LIST_ROWS = 8;

export function SchoolProfilesStats({
  data,
  loading,
}: {
  data: SchoolProfilesResponse | null;
  loading: boolean;
}): ReactElement {
  const totals = data?.data.totals ?? null;
  const busy = loading && data === null;

  return (
    <PiStatGrid>
      <PiStat
        label="Subscribed schools"
        value={formatCount(totals?.subscribedSchools)}
        detail={totals !== null ? `${formatCount(totals.publicProfiles)} public profiles` : undefined}
        loading={busy}
      />
      <PiStat
        label="Profile views"
        value={formatCount(totals?.profileViews)}
        detail={totals !== null ? `${formatCount(totals.profileVisitors)} visitors` : undefined}
        loading={busy}
      />
      <PiStat label="Engaged students" value={formatCount(totals?.engagedStudents)} loading={busy} />
      <PiStat label="Interactions" value={formatCount(totals?.interactions)} loading={busy} />
    </PiStatGrid>
  );
}

interface OverviewPartProps {
  data: SchoolProfilesResponse | null;
  loading: boolean;
}

/** Interaction mix, student countries and student sources: one three-column block. */
export function SchoolProfilesEngagement({ data, loading }: OverviewPartProps): ReactElement {
  const totals = data?.data.totals ?? null;
  const busy = loading && data === null;

  return (
    <div className="grid min-w-0 gap-3 lg:grid-cols-3">
      <BarList
        title="Interaction mix"
        rows={interactionRows(totals?.byType)}
        loading={busy}
        emptyText="No interactions in this cycle."
      />
      <BarList
        title="Student countries"
        rows={countryRows(data?.data.byCountry)}
        loading={busy}
        emptyText="No engaged students in this cycle."
        maxRows={LIST_ROWS}
      />
      <BarList
        title="Student sources"
        rows={sourceRows(data?.data.bySource)}
        loading={busy}
        emptyText="No engaged students in this cycle."
        maxRows={LIST_ROWS}
      />
    </div>
  );
}

/** Profile views by visitor country; `null` (renders nothing) unless the server reports it. */
export function SchoolProfilesViewsByCountry({ data, loading }: OverviewPartProps): ReactElement | null {
  const busy = loading && data === null;
  // Profile-view breakdowns: only when the server says they are ok; absent = hidden.
  const byCountry = data?.data.totals.viewsByCountry;
  if (busy || !viewsBreakdownOk(data?.meta) || byCountry === undefined) return null;
  return (
    <ViewsBarList
      title="Profile views by country"
      rows={byCountry === null ? null : viewsCountryRows(byCountry)}
      emptyText="No profile views in this cycle."
      maxRows={LIST_ROWS}
    />
  );
}

/** Profile views by channel; `null` (renders nothing) unless the server reports it. */
export function SchoolProfilesViewsByChannel({ data, loading }: OverviewPartProps): ReactElement | null {
  const busy = loading && data === null;
  const byChannel = data?.data.totals.viewsByChannel;
  if (busy || !viewsBreakdownOk(data?.meta) || byChannel === undefined) return null;
  return (
    <ViewsBarList
      title="Profile views by channel"
      rows={byChannel === null ? null : viewsChannelRows(byChannel)}
      emptyText="No profile views in this cycle."
      maxRows={LIST_ROWS}
    />
  );
}

/**
 * A profile-view breakdown list. `rows === null` means the server did not
 * report it: the list renders with a dash, never a zero.
 */
export function ViewsBarList({
  title,
  rows,
  emptyText,
  maxRows,
}: {
  title: string;
  rows: BarListRow[] | null;
  emptyText: string;
  maxRows?: number;
}): ReactElement {
  if (rows === null) return <BarList title={title} total={null} rows={[]} emptyText="—" />;
  return <BarList title={title} rows={rows} emptyText={emptyText} maxRows={maxRows} />;
}
